import _ from 'lodash';
import { adminClient as apolloClient, safeClearStore } from '@/store/apollo';
import axios from 'axios';

import AuthApi from '@/store/api/system/auth';
import { ensureFreshToken } from '@/helpers';

const PREVIEW_ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PREVIEW_ROUTE}`;

import PurchaseGql from '@/extensions/shop/store/graphql/queries/Purchase.gql';
// import PurchasesGql from '@/extensions/shop/store/graphql/queries/Purchases.gql';
import PurchasesPaginatedGql from '@/extensions/shop/store/graphql/queries/PurchasesPaginated.gql';
import PreviewSamplePurchasesGql from '@/extensions/shop/store/graphql/queries/PreviewSamplePurchases.gql';
import PreviewSampleCancellationsGql from '@/extensions/shop/store/graphql/queries/PreviewSampleCancellations.gql';
import PurchasesSoldByValuesGql from '@/extensions/shop/store/graphql/queries/PurchasesSoldByValues.gql';
// import CreatePurchaseGql from '@/extensions/shop/store/graphql/mutations/CreatePurchase.gql';
import UpdatePurchaseGql from '@/extensions/shop/store/graphql/mutations/UpdatePurchase.gql';
import CreatePurchaseGql from '@/extensions/shop/store/graphql/mutations/CreatePurchase.gql';
// import DeletePurchaseGql from '@/extensions/shop/store/graphql/mutations/DeletePurchase.gql';
import SendInvoice from '@/extensions/shop/store/graphql/mutations/SendInvoice.gql';
import SendPurchaseConfirmation from '@/extensions/shop/store/graphql/mutations/SendPurchaseConfirmation.gql';
import SendCreditNote from '@/extensions/shop/store/graphql/mutations/SendCreditNote.gql';
import ResendReturnEmail from '@/extensions/shop/store/graphql/mutations/ResendReturnEmail.gql';
import MarkCancellationReceived from '@/extensions/shop/store/graphql/mutations/MarkCancellationReceived.gql';
import MarkRefundIssued from '@/extensions/shop/store/graphql/mutations/MarkRefundIssued.gql';
import SendShippingPackageSent from '@/extensions/shop/store/graphql/mutations/SendShippingPackageSent.gql';

import PurchaseLogsGql from '@/extensions/shop/store/graphql/queries/PurchaseLogs.gql';
import ProcessPurchasePaginationBulkActionGql from '@/extensions/shop/store/graphql/mutations/ProcessPurchasePaginationBulkAction.gql';
import SendStatusEmail from '@/extensions/shop/store/graphql/mutations/SendStatusEmail.gql';
import CancelPurchasedItems from '@/extensions/shop/store/graphql/mutations/CancelPurchasedItems.gql';


export default {
    // Sample-purchase search for the document-template preview drawer, scoped to
    // a single shop (filters passed as a variable — see PreviewSamplePurchases.gql).
    fetchPreviewSamples: ({ shopId = null, search = null, posOnly = false } = {}) =>
        new Promise((resolve, reject) => {
            const filters = [];
            if (shopId) filters.push({ key: 'shop', match: shopId });
            if (search) filters.push({ key: 'invoice_nr', like: `%${search}%` });

            apolloClient
                .query({
                    query: PreviewSamplePurchasesGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        filters,
                        includePointOfSalePurchases: posOnly,
                        includeWebShopPurchases: !posOnly,
                    },
                })
                .then(({ data }) => resolve(_.get(data, 'CO_PurchasesPaginated.items', [])))
                .catch(err => reject(err));
        }),

    // Sample-cancellation search (purchases that have cancellations), scoped to a
    // single shop — see PreviewSampleCancellations.gql.
    fetchPreviewCancellationSamples: ({ shopId = null, search = null } = {}) =>
        new Promise((resolve, reject) => {
            const filters = [];
            if (shopId) filters.push({ key: 'shop', match: shopId });
            if (search) filters.push({ key: 'invoice_nr', like: `%${search}%` });

            apolloClient
                .query({ query: PreviewSampleCancellationsGql, fetchPolicy: 'network-only', variables: { filters } })
                .then(({ data }) => resolve(_.get(data, 'CO_PurchasesPaginated.items', [])))
                .catch(err => reject(err));
        }),

    fetchPurchasesPaginated: (data, fetchPolicy = 'cache-first') =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: PurchasesPaginatedGql,
                    fetchPolicy,
                    variables: {
                        // Multistore: the store the admin UI currently has selected; the list
                        // shows its records by default and the store filter widens it again.
                        cms: _.get(data, 'cms') ? parseInt(_.get(data, 'cms')) : null,
                        scope: _.get(data, 'scope', 'ex'),
                        type: _.get(data, 'type', 'shop_purchase'),
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        filters,

                        // custom
                        includeWebShopPurchases: _.get(data, 'includeWebShopPurchases', true),
                        includeInternals: _.get(data, 'includeInternals', false),
                        includePointOfSalePurchases: _.get(data, 'includePointOfSalePurchases', false),
                        includeMarketplacePurchases: _.get(data, 'includeMarketplacePurchases', true),

                        includeItems: _.get(data, 'includeItems', false),
                        includeImages: _.get(data, 'includeImages', false),
                        includeCoupons: _.get(data, 'includeCoupons', false),
                    }
                })
                // .then(({ data }) => resolve(data.CO_PurchasesPaginated))
                .then(({ data }) => resolve(data.CO_PurchasesPaginated))
                .catch(err => reject(err));
        }),
    // Which store an order belongs to - all a refused detail page needs to explain
    // itself and offer the switch.

    fetchPurchase: (variables, fetchPolicy = 'cache-first') => {
        if (variables.id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            if (variables.id) variables.id = parseInt(variables.id);
            if (variables.pointOfSale) variables.pointOfSale = parseInt(variables.pointOfSale);
            // Multistore: an order belongs to one store, so the detail is opened in
            // that store's context - the API refuses it from another one.
            if (variables.cms) variables.cms = parseInt(variables.cms);

            apolloClient
                .query({
                    query: PurchaseGql,
                    fetchPolicy,
                    variables
                })
                .then(({ data }) => resolve(data.CO_Purchase))
                .catch(err => reject(err));
        });
    },
    fetchPurchaseLogs: (id, shopID = null) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            let variables = { id: parseInt(id) };
            if (shopID) variables.shop = parseInt(shopID);

            apolloClient
                .query({
                    query: PurchaseLogsGql,
                    fetchPolicy: 'no-cache',
                    variables
                })
                .then(({ data }) => resolve(data.CO_Purchase.statusLogs))
                .catch(err => reject(err));
        });
    },
    create: purchase => {
        if (purchase === undefined) throw new Error('required argument is missing');

        purchase = _.removeDeepByKey(purchase, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreatePurchaseGql,
                    variables: { purchase }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_CreatePurchase);
                })
                .catch(err => reject(err));
        });
    },
    update: purchase => {
        if (purchase === undefined) throw new Error('required argument is missing');

        purchase = _.removeDeepByKey(purchase, '__typename');
        // @TODO: anstatt removeByKey, sollte gar nicht in den State
        purchase = _.removeDeepByKey(purchase, 'cms');
        if (purchase.errors !== undefined) delete purchase.errors;

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdatePurchaseGql,
                    variables: { purchase }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_UpdatePurchase);
                })
                .catch(err => reject(err));
        });
    },
    // delete: id => {
    //     if (id === undefined) throw new Error('required argument is missing');
    //
    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .mutate({
    //                 mutation: DeletePurchaseGql,
    //                 variables: { id }
    //             })
    //             .then(({ data }) => {
    //                 apolloClient.clearStore();
    //                 resolve(data.CO_DeletePurchase);
    //             })
    //             .catch(err => reject(err));
    //     });
    // }
    cancelPurchasedItems: (cancellation) => {
        if (cancellation === undefined) throw new Error('required argument is missing');

        cancellation = _.removeDeepByKey(cancellation, '__typename');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CancelPurchasedItems,
                    variables: { cancellation }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_CancelPurchasedItems);
                })
                .catch(err => reject(err));
        });
    },
    sendInvoice: purchase => {
        if (purchase === undefined) throw new Error('required argument is missing');

        purchase = _.removeDeepByKey(purchase, '__typename');
        // @TODO: anstatt removeByKey, sollte gar nicht in den State
        purchase = _.removeDeepByKey(purchase, 'cms');
        if (purchase.errors !== undefined) delete purchase.errors;

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendInvoice,
                    variables: { purchase }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_SendInvoice);
                })
                .catch(err => reject(err));
        });
    },
    sendConfirmation: purchase => {
        if (purchase === undefined) throw new Error('required argument is missing');

        purchase = _.removeDeepByKey(purchase, '__typename');
        // @TODO: anstatt removeByKey, sollte gar nicht in den State
        purchase = _.removeDeepByKey(purchase, 'cms');
        if (purchase.errors !== undefined) delete purchase.errors;

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendPurchaseConfirmation,
                    variables: { purchase }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_SendPurchaseConfirmation);
                })
                .catch(err => reject(err));
        });
    },
    sendCreditNote: cancellation => {
        if (cancellation === undefined) throw new Error('required argument is missing');

        cancellation = _.removeDeepByKey(cancellation, '__typename');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendCreditNote,
                    variables: { cancellation }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data.CO_SendCreditNote);
                })
                .catch(err => reject(err));
        });
    },
    resendReturnEmail: (cancellationId, scope) => {
        if (cancellationId === undefined || !scope) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ResendReturnEmail,
                    variables: { cancellation_id: parseInt(cancellationId), scope }
                })
                .then(async ({ data }) => {
                    await safeClearStore(apolloClient);
                    resolve(data.CO_ResendReturnEmail);
                })
                .catch(err => reject(err));
        });
    },
    markCancellationReceived: (cancellationId, items) => {
        if (cancellationId === undefined || !Array.isArray(items)) throw new Error('required argument is missing');
        items = items.map(item => _.removeDeepByKey({ ...item }, '__typename'));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: MarkCancellationReceived,
                    variables: { cancellation_id: parseInt(cancellationId), items },
                })
                .then(async ({ data }) => {
                    await safeClearStore(apolloClient);
                    resolve(data.CO_MarkCancellationReceived);
                })
                .catch(err => reject(err));
        });
    },
    markRefundIssued: (cancellationId, amount, note) => {
        if (cancellationId === undefined || !note) throw new Error('required argument is missing');
        const variables = { cancellation_id: parseInt(cancellationId), note };
        if (amount !== null && amount !== undefined && amount !== '') {
            variables.amount = parseFloat(amount);
        }
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkRefundIssued, variables })
                .then(async ({ data }) => {
                    await safeClearStore(apolloClient);
                    resolve(data.CO_MarkRefundIssued);
                })
                .catch(err => reject(err));
        });
    },
    sendShippingPackageSent: shippingPackage => {
        if (shippingPackage === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendShippingPackageSent,
                    variables: { id: parseInt(shippingPackage) }
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data);
                })
                .catch(err => reject(err));
        });
    },
    sendStatusEmail: variables => {

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SendStatusEmail,
                    variables
                })
                .then(async ({ data }) => {
                    // await apolloClient.clearStore();
                    // // NOTE: timeout -> workaround for Invariant Violation
                    // await new Promise(cb => setTimeout(cb, 10));
                    await safeClearStore(apolloClient);
                    resolve(data);
                })
                .catch(err => reject(err));
        });
    },
    confirmationRenderingUrl(id, type = 'email') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}purchase-confirmation-${type}/${id}?token=${token}`;
    },
    invoiceRenderingUrl(id, type = 'email') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}invoice-${type}/${id}?token=${token}`;
    },
    creditNoteRenderingUrl(id, type = 'email') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}credit-note-${type}/${id}?token=${token}`;
    },
    deliveryNoteRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}delivery-note/${id}?token=${token}`;
    },
    packingListRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}packing-list/${id}?token=${token}`;
    },
    returnFormRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-form/${id}?token=${token}`;
    },
    returnSlipRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-slip/${id}?token=${token}`;
    },
    returnReceivedRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-received/${id}?token=${token}`;
    },
    returnStatusChangedRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-status-changed/${id}?token=${token}`;
    },
    returnReminderRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-reminder/${id}?token=${token}`;
    },
    returnLookupLinkRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}return-lookup-link/${id}?token=${token}`;
    },
    shippingPackageSlipRenderingUrl(id) {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}shipping-package-slip/${id}?token=${token}`;
    },
    receiptRenderingUrl(id, action = 'preview') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}cash-receipt/${id}?${action}&token=${token}`;
    },
    shippingPackageSentRenderingUrl(id, action = 'preview') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}shipping-package-sent/${id}?${action}&token=${token}`;
    },
    couponRenderingUrl(id, type = 'letter') {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}coupon-${type}/${id}?token=${token}`;
    },
    renderInvoice(id, type = 'email') {
        return new Promise((resolve, reject) => {
        ensureFreshToken()
            .then(() => axios.get(this.invoiceRenderingUrl(id, type)))
            .then(result => resolve(result))
            .catch(err => reject(err));
        });
    },
    renderCreditNote(id, type = 'email') {
        return new Promise((resolve, reject) => {
        ensureFreshToken()
            .then(() => axios.get(this.creditNoteRenderingUrl(id, type)))
            .then(result => resolve(result))
            .catch(err => reject(err));
        });
    },
    renderConfirmation(id, type = 'email') {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.confirmationRenderingUrl(id, type)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    renderReturnReceived(id) {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.returnReceivedRenderingUrl(id)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    renderReturnStatusChanged(id) {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.returnStatusChangedRenderingUrl(id)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    renderReturnReminder(id) {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.returnReminderRenderingUrl(id)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    renderReturnLookupLink(id) {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.returnLookupLinkRenderingUrl(id)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    renderShippingPackageSent(id, type = 'email') {
        return new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(this.shippingPackageSentRenderingUrl(id, type)))
                .then(result => resolve(result))
                .catch(err => reject(err));
        });
    },
    processPurchasePaginationBulkAction: (action, payload, variables, data) => {
        return new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .mutate({
                    mutation: ProcessPurchasePaginationBulkActionGql,
                    variables: {
                        action,
                        payload: JSON.stringify(payload),
                        filters,
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),

                        // custom
                        includeWebShopPurchases: _.get(variables, 'includeWebShopPurchases', true),
                        includeInternals: _.get(variables, 'includeInternals', false),
                        includePointOfSalePurchases: _.get(variables, 'includePointOfSalePurchases', false),
                        includeMarketplacePurchases: _.get(variables, 'includeMarketplacePurchases', true),
                    },
                })
                .then(({ data }) => resolve(data.CO_ProcessPurchasePaginationBulkAction))
                .catch(err => reject(err));
        });
    },
    fetchSoldByValues: (shopId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: PurchasesSoldByValuesGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        shop: parseInt(shopId),
                    },
                })
                .then(({ data }) => resolve(data.CO_PurchasesSoldByValues || []))
                .catch(err => reject(err));
        }),
};
